import { Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const PricingCard = ({ name, price, period, description, features, featured }) => (
  <div
    className={`relative bg-white border rounded-xl p-8 md:p-10 flex flex-col h-full ${
      featured
        ? 'pricing-featured border-primary'
        : 'border-gray-200 hover:border-primary/50 hover:shadow-lg transition-all duration-300'
    }`}
  >
    {featured && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <span className="bg-primary text-white text-xs font-bold uppercase tracking-widest px-4 py-1 rounded-full">
          Most Popular
        </span>
      </div>
    )}
    <h3 className="font-oswald uppercase text-lg font-bold text-gray-900">
      {name}
    </h3>
    <div className="mt-4">
      <span className="font-oswald text-4xl md:text-5xl font-bold text-gray-900">
        {price}
      </span>
      <span className="font-manrope text-gray-500">{period}</span>
    </div>
    <p className="font-manrope text-sm text-gray-500 mt-4">
      {description}
    </p>
    <ul className="mt-6 space-y-3 flex-grow">
      {features}
    </ul>
    <div className="mt-8">
      <a
        href="https://calendly.com/atabdullah-mah/30min"
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        <Button
          className={`w-full rounded-full py-6 text-sm font-bold uppercase tracking-wide btn-scale flex items-center justify-center gap-2 ${
            featured
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-primary hover:text-primary'
          }`}
        >
          Get Started
          <ArrowRight size={16} />
        </Button>
      </a>
    </div>
  </div>
);

const FeatureItem = ({ text }) => (
  <li className="font-manrope text-sm text-gray-700 flex items-start gap-3">
    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
    {text}
  </li>
);

export const RetainerModel = () => {
  return (
    <section
      data-testid="retainer-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <span className="font-manrope uppercase tracking-widest text-xs font-semibold text-primary">
            Pricing
          </span>
          <h2
            className="font-oswald uppercase tracking-tight font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mt-4"
            data-testid="retainer-headline"
          >
            <span className="text-gray-900">We work on retainers.</span>
            <br />
            <span className="text-primary">Because systems need ownership.</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-gray-500 mt-6">
            We don't sell one-off campaigns or viral promises. We partner long-term to design, operate, and evolve growth systems.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div data-testid="pricing-card-1">
            <PricingCard
              name="Growth Foundation"
              price="from $10K"
              period="/ month"
              description="For brands and creators building a serious social media base."
              featured={false}
              features={
                <>
                  <FeatureItem text="Strategy & architecture" />
                  <FeatureItem text="Content engine setup" />
                  <FeatureItem text="Platform-native execution" />
                  <FeatureItem text="And additional foundational support based on your goals and platform maturity" />
                </>
              }
            />
          </div>

          <div data-testid="pricing-card-2">
            <PricingCard
              name="Platform-Led Growth System"
              price="$20K–$30K"
              period="/ month"
              description="For businesses ready to scale across platforms."
              featured={true}
              features={
                <>
                  <FeatureItem text="Advanced content velocity" />
                  <FeatureItem text="Creator partnerships" />
                  <FeatureItem text="Cross-platform amplification" />
                  <FeatureItem text="Paid + organic integration" />
                  <FeatureItem text="And additional growth initiatives based on performance signals and priorities" />
                </>
              }
            />
          </div>

          <div data-testid="pricing-card-3">
            <PricingCard
              name="Platform Advantage Retainer"
              price="$40K–$50K"
              period="/ month"
              description="For brands, founders, and public figures seeking leverage."
              featured={false}
              features={
                <>
                  <FeatureItem text="Monetization systems" />
                  <FeatureItem text="LIVE pilots & experimentation" />
                  <FeatureItem text="Executive visibility" />
                  <FeatureItem text="Market entry & positioning" />
                  <FeatureItem text="And bespoke strategic initiatives aligned with your market, scale, and expansion goals" />
                </>
              }
            />
          </div>
        </div>

        {/* Note */}
        <p className="font-manrope text-sm text-gray-500 text-center mt-12">
          Each engagement is customized based on goals, market, and maturity.
        </p>
      </div>
    </section>
  );
};
